import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as DeliveryContext } from '../../../../context/delivery';
import { Context as RestaurantContext } from '../../../../context/restaurant';
import CheckoutPayment from '../payment';
import CheckoutTotal from '../total';
import ContentWrapper from '../../../../layouts/delivery/contentWrapper';
import ConfirmButton from '../../../../components/buttons/confirmButton';
import CartItem from '../../../cart/components/cartItem';
import DeliveryMethod from './deliveryMethod';
import TextInput, { inputType } from '../../../../components/inputs/textInput';
import TopBar from '../../../../layouts/delivery/topBar';
import { useDeliveryCheckoutForm } from '../../hooks/useDeliveryCheckoutForm';
import { useDeliveryColors } from '../../../../hooks/useDeliveryColors';
import { useDeliveryPricing } from '../../hooks/useDeliveryPricing';
import { getDeliveryDateOnDeliverNow } from '../../services/deliveryTimeHelpers';
import { getUtcFromTimezoneMillis } from '../../../../utils/dateHelpers';
import { useValidator, validationTypes } from '../../../../hooks/useValidator';

const CheckoutBillDelivery = ({
  deliveryRestaurant,
  bill,
  onPay,
  paymentOption,
  disabled,
  onFail,
  paymentKey,
}) => {
  const { t } = useTranslation(['translation', 'delivery']);

  const navigate = useNavigate();
  const colors = useDeliveryColors();

  const {
    onBannerHeightChange,
    isCheckoutAbsoluteAtBottom,
    isCheckoutFixed,
    contentWrapperRef,
    movableCheckoutBoxRef,
    checkoutMarginsRef,
  } = useDeliveryCheckoutForm();

  const { state: deliveryState } = useContext(DeliveryContext);
  const { state: restaurantState } = useContext(RestaurantContext);

  // think no tips?
  const [tipsAmount] = useState(0);

  const [isTakeAway, setIsTakeAway] = useState(
    deliveryRestaurant.hasTakeaway && deliveryState.defaultDeliveryType !== 'delivery',
  );
  const [isDeliverNow, setIsDeliverNow] = useState(true);
  const [deliveryDateMillis, setDeliveryDateMillis] = useState(0);

  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [noteForDriver, setNoteForDriver] = useState('');

  const [invalidChildOptions, setInvalidChildOptions] = useState({});

  const cardFormSubmitRef = useRef();

  const {
    subtotal,
    total,
    totalDeliveryPrice,
    totalDiscount,
    totalPackagingPrice,
    totalReachMinimalPrice,
  } = useDeliveryPricing({
    bill,
    deliveryRestaurant,
    isTakeAway,
    productCategories: restaurantState.productCategories,
    tipsAmount,
  });

  const { isValid: isEmailValid, validate: validateEmail } = useValidator({
    type: validationTypes.email,
    value: email,
  });
  const { isValid: isPhoneValid, validate: validatePhone } = useValidator({
    type: validationTypes.phone,
    value: phone,
  });

  if (!bill) {
    return null;
  }

  // 1: canPlace, 2: errorMessage
  const canPlaceAnOrder = () => {
    if (invalidChildOptions.noAvailableDeliveryTypeOptions) {
      return { isOrderDisabled: true, orderButtonError: t('delivery:checkout.noDeliveryTypes') };
    }

    if (invalidChildOptions.noAvailableDeliveryOptions) {
      return { isOrderDisabled: true, orderButtonError: t('delivery:checkout.noDeliveryTimes') };
    }

    if (!selectedPaymentMethodType || !total) {
      return { isOrderDisabled: true };
    }

    if (isTakeAway && (!email || !phone || !isPhoneValid || !isEmailValid)) {
      return { isOrderDisabled: true };
    }

    if (
      !isTakeAway &&
      (!email || !phone || !deliveryAddress || !city || !zipCode || !isPhoneValid || !isEmailValid)
    ) {
      return { isOrderDisabled: true };
    }

    return { isOrderDisabled: false };
  };

  const { isOrderDisabled, orderButtonError } = canPlaceAnOrder();

  const onPayWrapper = ({ paymentType, paymentMethodId }) => {
    const isEmailValidOnSubmit = validateEmail();
    const isPhoneValidOnSubmit = validatePhone();

    if (!isEmailValidOnSubmit || !isPhoneValidOnSubmit) {
      return;
    }

    onPay({
      paymentType,
      tips: tipsAmount,
      paymentMethodId,
      employeeId: null,
      delivery: {
        isTakeAway,
        email,
        phone: phone ? phone.replace(/[^\d+]/g, '') : '',
        address: deliveryAddress,
        city,
        zipCode,
        noteForDriver,
        isDeliveryNow: isDeliverNow,
        deliveryDate: isDeliverNow
          ? getDeliveryDateOnDeliverNow({ deliveryRestaurant, isTakeAway })
          : getUtcFromTimezoneMillis(deliveryDateMillis, deliveryRestaurant.data.timeZone),
      },
    });
  };

  const renderCheckout = (isForLargeScreen) => {
    return (
      <div
        ref={isForLargeScreen ? movableCheckoutBoxRef : undefined}
        className={
          !isForLargeScreen
            ? 'mt-12 bg-white px-8 pt-12 pb-10 border-input border rounded-md md:hidden z-20'
            : `bg-white px-8 pt-12 pb-10 border-input border
                rounded-md hidden md:block md:w-[308px] lg:w-[400px] z-20
                ${isCheckoutFixed ? 'fixed' : 'absolute'}`
        }
        style={
          isForLargeScreen
            ? {
                // eslint-disable-next-line no-nested-ternary
                top: isCheckoutAbsoluteAtBottom
                  ? checkoutMarginsRef.topAtBottom
                  : isCheckoutFixed
                  ? 20
                  : checkoutMarginsRef.top,
                right: checkoutMarginsRef.right,
              }
            : undefined
        }
      >
        <CheckoutTotal
          subtotal={subtotal}
          total={total}
          tips={tipsAmount}
          discount={totalDiscount}
          delivery={totalDeliveryPrice}
          minimalRemaining={totalReachMinimalPrice}
          packaging={totalPackagingPrice}
        />
        <div className="mt-10" />
        <ConfirmButton
          onClick={() => {
            if (selectedPaymentMethodType === 'Card' && paymentOption.cardProvider === 'Stripe') {
              if (!cardFormSubmitRef.current) {
                return;
              }

              cardFormSubmitRef.current.requestSubmit();
            } else {
              onPayWrapper({
                paymentType: selectedPaymentMethodType,
                paymentMethodId: selectedPaymentMethodId,
              });
            }
          }}
          customText={orderButtonError || t('delivery:buttons.orderAndPay')}
          noShadow
          noSpacing
          disabled={isOrderDisabled}
          loading={disabled}
          customColor={colors.hasCustomPrimary ? colors.primary : undefined}
        />
      </div>
    );
  };

  return (
    <>
      <TopBar
        customTitle={t('delivery:checkout.title')}
        onBack={() => {
          navigate(`/${deliveryState.slug}/${deliveryRestaurant.slug}`, { replace: true });
        }}
        onBannerHeightChange={onBannerHeightChange}
      />
      <ContentWrapper>
        <div
          className="flex flex-col lg:flex-row justify-between items-start"
          ref={contentWrapperRef}
        >
          <div className="md:max-w-sm lg:max-w-lg xl:max-w-xl w-full mr-9">
            <h3 className="font-primarySemiBold text-primary text-xl mt-14 mb-5">
              {t('delivery:checkout.selectedItems')}
            </h3>
            <div>
              {bill &&
                bill.cart &&
                bill.cart.map((item) => (
                  <div key={`${item.productId}-${item.index}`} className="mb-2">
                    <CartItem product={item} fromCheckout />
                  </div>
                ))}
            </div>
            <button
              type="button"
              onClick={() => {
                navigate(`/${deliveryState.slug}/menu`, { replace: true });
              }}
              className="font-primaryMedium text-base text-primary mt-3"
            >
              {t('delivery:checkout.addMoreItems')} +
            </button>
            <h3 className="font-primarySemiBold text-primary text-xl mt-12 mb-5">
              {t('delivery:checkout.deliveryMethodAndTime')}
            </h3>
            <DeliveryMethod
              isTakeAway={isTakeAway}
              onChange={setIsTakeAway}
              onTimeChange={(isDeliverNowVal, deliveryDateVal) => {
                setIsDeliverNow(isDeliverNowVal);
                setDeliveryDateMillis(deliveryDateVal);
              }}
              deliveryRestaurant={deliveryRestaurant}
              onInvalidOptions={(key, val) => {
                setInvalidChildOptions((prev) => ({ ...prev, [key]: val }));
              }}
            />
            <h3 className="font-primarySemiBold text-primary text-xl mt-12 mb-5">
              {t('delivery:checkout.yourContactDetails')}:
            </h3>
            <div>
              <TextInput
                id="order-email"
                onChange={setEmail}
                value={email}
                type={inputType.formField}
                label={t('delivery:checkout.email')}
                required
                isDelivery
                isError={!isEmailValid}
              />
              <div className="mt-5" />
              <TextInput
                id="order-phone"
                onChange={setPhone}
                value={phone}
                type={inputType.phoneFormField}
                label={t('delivery:checkout.phone')}
                defaultCountryCode="lt"
                required
                isDelivery
                isError={!isPhoneValid}
              />
              {!isTakeAway && (
                <div>
                  <div className="mt-5" />
                  <TextInput
                    id="order-address"
                    onChange={setDeliveryAddress}
                    value={deliveryAddress}
                    type={inputType.formField}
                    label={t('delivery:checkout.deliveryAddress')}
                    required
                    isDelivery
                  />
                  <div className="mt-5" />
                  <TextInput
                    id="order-city"
                    onChange={setCity}
                    value={city}
                    type={inputType.formField}
                    label={t('delivery:checkout.city')}
                    required
                    isDelivery
                  />
                  <div className="mt-5" />
                  <TextInput
                    id="order-zip"
                    onChange={setZipCode}
                    value={zipCode}
                    type={inputType.formField}
                    label={t('delivery:checkout.zipCode')}
                    required
                    isDelivery
                  />
                  <div className="mt-5" />
                  <TextInput
                    id="order-note"
                    onChange={setNoteForDriver}
                    value={noteForDriver}
                    type={inputType.formField}
                    label={t('delivery:checkout.noteForDriver')}
                    isDelivery
                  />
                </div>
              )}
            </div>
            <h3 className="font-primarySemiBold text-primary text-xl mt-12 mb-5">
              {t('delivery:checkout.choosePaymentMethod')}:
            </h3>
            <CheckoutPayment
              paymentOption={paymentOption}
              // non redirect link payments only
              onPay={(paymentType, paymentMethodId) => {
                onPayWrapper({ paymentType, paymentMethodId });
              }}
              total={total}
              onFail={onFail}
              paymentKey={paymentKey}
              disabled={isOrderDisabled}
              loading={disabled}
              isDelivery
              selectedDeliveryPayment={selectedPaymentMethodType}
              onSelectDeliveryOption={(paymentType, paymentMethodId) => {
                setSelectedPaymentMethodType(paymentType);
                setSelectedPaymentMethodId(paymentMethodId);
              }}
              cardFormSubmitRef={cardFormSubmitRef}
            />

            {renderCheckout(false)}
          </div>
        </div>
        {renderCheckout(true)}
      </ContentWrapper>
    </>
  );
};

export default CheckoutBillDelivery;
